import * as React from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { NavUser } from "@/components/nav-user";
import { useTheme } from "@/components/theme-provider";
import { FeedbackDialog } from "@/components/feedback-dialog";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from "@/components/ui/sidebar";
import {
  RiDashboardLine,
  RiDatabase2Line,
  RiFileCodeLine,
  RiHistoryLine,
  RiBookmarkLine,
  RiTeamLine,
  RiSettings3Line,
  RiSparklingLine,
  RiArrowDownSLine,
  RiArrowRightSLine,
  RiBarChartBoxLine,
  RiShieldLine,
  RiKeyLine,
  RiNotificationLine,
  RiBellLine,
  RiUserLine,
  RiToolsLine,
  RiCodeLine,
  RiFolderLine,
  RiTableLine,
  RiFlowChart,
  RiCheckLine,
  RiLayoutGridLine,
  RiQuestionLine,
} from "@remixicon/react";
import { useSession } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";

const navItems = [
  {
    title: "Workspace",
    items: [
      {
        title: "Dashboard",
        url: "/dashboard",
        icon: RiDashboardLine,
      },
      {
        title: "Databases",
        url: "/databases",
        icon: RiDatabase2Line,
        isCollapsible: true,
        subItems: [
          {
            title: "Connections",
            url: "/databases",
          },
          {
            title: "Data Browser",
            url: "/data-browser",
          },
        ],
      },
      {
        title: "Query Editor",
        url: "/query",
        icon: RiCodeLine,
      },
      {
        title: "Visual Builder",
        url: "/visual-query-builder",
        icon: RiFlowChart,
      },
      {
        title: "Schema Designer",
        url: "/visual-schema-designer",
        icon: RiLayoutGridLine,
      },
      {
        title: "Templates",
        url: "/templates/explore",
        icon: RiFolderLine,
      },
      {
        title: "Saved Queries",
        url: "/saved-queries",
        icon: RiBookmarkLine,
      },
      {
        title: "Query History",
        url: "/history",
        icon: RiHistoryLine,
      },
      {
        title: "Analytics",
        url: "/analytics",
        icon: RiBarChartBoxLine,
      },
    ],
  },
  {
    title: "Management",
    items: [
      {
        title: "Team",
        url: "/team",
        icon: RiTeamLine,
      },
      {
        title: "Security",
        url: "/security",
        icon: RiShieldLine,
      },
    ],
  },
  {
    title: "Settings",
    items: [
      {
        title: "Preferences",
        url: "/settings",
        icon: RiSettings3Line,
      },
      {
        title: "Help",
        url: "/help",
        icon: RiQuestionLine,
      },
    ],
  },
];

function SidebarLogo() {
  const { theme, systemTheme } = useTheme();
  const currentTheme = theme === 'system' ? systemTheme : theme;
  const logoSrc = currentTheme === 'dark' ? '/moniva-light.svg' : '/moniva-dark.svg';

  return (
    <div className="flex gap-2 px-2 group-data-[collapsible=icon]:px-0 transition-[padding] duration-200 ease-in-out">
      <Link to="/dashboard" className="inline-flex items-center gap-2 group/logo">
        <img src={logoSrc} alt="Moniva" className="w-8 h-8" />
        <span className="font-bold text-lg group-data-[collapsible=icon]:hidden">
          Moniva
        </span>
      </Link>
    </div>
  );
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { data: session } = useSession();
  const location = useLocation();
  const [openItems, setOpenItems] = React.useState<string[]>([]);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [organizations, setOrganizations] = React.useState<any[]>([]);
  const [currentOrganization, setCurrentOrganization] = React.useState<any>(null);
  const [isLoadingOrgs, setIsLoadingOrgs] = React.useState(true);

  // Load user's organizations
  React.useEffect(() => {
    if (session?.user?.id) {
      loadOrganizations();
    }
  }, [session?.user?.id]);

  const loadOrganizations = async () => {
    if (!session?.user?.id) return;

    try {
      setIsLoadingOrgs(true);
      const result = await api.getUserOrganizations();
      setOrganizations(result.organizations);

      // Set the first organization as current if none is selected
      if (result.organizations.length > 0 && !currentOrganization) {
        setCurrentOrganization(result.organizations[0]);
      }
    } catch (error) {
      console.error('Failed to load organizations:', error);
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  const toggleItem = (title: string) => {
    setOpenItems((prev) =>
      prev.includes(title) ? prev.filter((item) => item !== title) : [...prev, title]
    );
  };

  return (
    <Sidebar collapsible="icon" variant="inset" {...props}>
      <SidebarHeader className="justify-center h-16 mb-2 max-md:mt-2">
        <SidebarLogo />
      </SidebarHeader>

      {/* Organization Selector */}
      {!isLoadingOrgs && organizations.length > 0 && (
        <div className="px-2 pb-2 border-b border-border/40 group-data-[collapsible=icon]:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between gap-2 h-9 px-3 text-sm font-medium bg-background/50 hover:bg-accent/50 border-border/60"
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="flex items-center justify-center size-5 rounded bg-primary/10 flex-shrink-0">
                    <span className="text-xs font-bold text-primary">
                      {currentOrganization?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="truncate">
                    {currentOrganization?.name || 'Select Organization'}
                  </span>
                </div>
                <RiArrowDownSLine className="size-4 text-muted-foreground flex-shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[240px]">
              {organizations.map((org) => (
                <DropdownMenuItem
                  key={org.id}
                  onClick={() => setCurrentOrganization(org)}
                  className="gap-2 cursor-pointer"
                >
                  <div className="flex items-center justify-center size-5 rounded bg-primary/10 flex-shrink-0">
                    <span className="text-xs font-bold text-primary">
                      {org.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <span className="flex-1 truncate">{org.name}</span>
                  {currentOrganization?.id === org.id && (
                    <RiCheckLine className="size-4 text-primary" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <SidebarContent className="-mt-2 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {navItems.map((group) => (
          <SidebarGroup key={group.title} className="py-0 mb-4">
            <SidebarGroupLabel className="uppercase text-muted-foreground/70 dark:text-zinc-400 text-[10px] font-semibold tracking-wider px-3 h-8 mb-1">
              {group.title}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu className="gap-1">
                {group.items.map((item) => {
                  const isActive = location.pathname === item.url ||
                    (item.url !== '/dashboard' && location.pathname.startsWith(item.url));
                  const isOpen = openItems.includes(item.title);

                  if (item.isCollapsible && item.subItems) {
                    return (
                      <Collapsible key={item.title} open={isOpen} onOpenChange={() => toggleItem(item.title)}>
                        <SidebarMenuItem>
                          <CollapsibleTrigger asChild>
                            <SidebarMenuButton
                              className="group/menu-button group-data-[collapsible=icon]:px-[5px] font-medium gap-3 h-9 px-3 [&>svg]:size-auto transition-all active:scale-95 hover:bg-accent/80"
                              tooltip={item.title}
                              isActive={isActive}
                            >
                              {item.icon && (
                                <item.icon
                                  className="text-muted-foreground/60 dark:text-zinc-400 group-data-[active=true]/menu-button:text-primary transition-colors"
                                  size={18}
                                  strokeWidth={2}
                                  aria-hidden="true"
                                />
                              )}
                              <span className="text-sm text-muted-foreground/80 dark:text-zinc-200 group-data-[active=true]/menu-button:text-foreground group-data-[active=true]/menu-button:font-semibold transition-colors">
                                {item.title}
                              </span>
                              {isOpen ? (
                                <RiArrowDownSLine className="ml-auto size-4 text-muted-foreground/50 transition-transform" />
                              ) : (
                                <RiArrowRightSLine className="ml-auto size-4 text-muted-foreground/50 transition-transform" />
                              )}
                            </SidebarMenuButton>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarMenuSub className="ml-3 border-l pl-3 pr-0 py-0.5">
                              {item.subItems.map((subItem) => {
                                const isSubActive = location.pathname === subItem.url;
                                return (
                                  <SidebarMenuSubItem key={subItem.title}>
                                    <SidebarMenuSubButton
                                      asChild
                                      className="h-7 px-3 text-xs text-muted-foreground/70 dark:text-zinc-300 hover:text-foreground transition-all active:scale-95 data-[active=true]:text-foreground data-[active=true]:font-medium"
                                      isActive={isSubActive}
                                    >
                                      <Link to={subItem.url}>{subItem.title}</Link>
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                );
                              })}
                            </SidebarMenuSub>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    );
                  }

                  return (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        className="group/menu-button group-data-[collapsible=icon]:px-[5px] font-medium gap-3 h-9 px-3 [&>svg]:size-auto transition-all active:scale-95 hover:bg-accent/80"
                        tooltip={item.title}
                        isActive={isActive}
                      >
                        <Link to={item.url}>
                          {item.icon && (
                            <item.icon
                              className="text-muted-foreground/60 dark:text-zinc-400 group-data-[active=true]/menu-button:text-primary transition-colors"
                              size={18}
                              strokeWidth={2}
                              aria-hidden="true"
                            />
                          )}
                          <span className="text-sm text-muted-foreground/80 dark:text-zinc-200 group-data-[active=true]/menu-button:text-foreground group-data-[active=true]/menu-button:font-semibold transition-colors">
                            {item.title}
                          </span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
      <SidebarFooter className="gap-2">
        {/* Feedback Widget */}
        <div
          className="mx-2 mb-2 p-3 rounded-lg bg-primary/5 border border-primary/20 group-data-[collapsible=icon]:hidden hover:bg-primary/10 transition-colors cursor-pointer"
          onClick={() => setFeedbackOpen(true)}
        >
          <div className="flex items-start gap-2 mb-2">
            <div className="flex items-center justify-center flex-shrink-0 rounded-md size-8 bg-primary/10">
              <RiSparklingLine className="size-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground">Got Feedback?</p>
              <p className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                Help us improve Moniva with your suggestions
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="w-full text-xs h-7 border-primary/30 hover:bg-primary/10"
            onClick={(e) => {
              e.stopPropagation();
              setFeedbackOpen(true);
            }}
          >
            Share Feedback
          </Button>
        </div>


        <NavUser
          user={{
            name: session?.user?.name || "User",
            email: session?.user?.email || "",
            avatar: session?.user?.image || undefined,
            // avatar: session.user.image || `https://api.dicebear.com/7.x/avataaars/svg?seed=${session.user.email}`,
          }}
        />
      </SidebarFooter>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </Sidebar>
  );
}
